import React, { useState, useEffect } from "react";
import { Button, Input, Table, Modal, ModalHeader, ModalBody, ModalFooter, Row, Col } from "reactstrap";
import axios from "axios";
import { END_POINT } from "../../../constants/defaultValues";
import { AddCommaNumber, NotificatioSucController, NotificatioErrController } from "../../../helpers/Utils";
import moment from "moment";
import classnames from "classnames";

const getInitials = (name) => {
  if (!name) return "";
  const parts = name.trim().split(" ");
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0].charAt(0) + parts[parts.length - 1].charAt(0)).toUpperCase();
};

const getAvatarBg = (name) => {
  if (!name) return "#8f9bb3";
  const colors = [
    "#a180ff",
    "#ffaa00",
    "#00d68f",
    "#ff3d71",
    "#3366ff",
    "#00d6d6",
    "#ff7f50",
  ];
  let sum = 0;
  for (let i = 0; i < name.length; i++) {
    sum += name.charCodeAt(i);
  }
  return colors[sum % colors.length];
};

const getContactValue = (value) => String(value || "").trim();

const ContactAction = ({ icon, label, value, href }) => {
  const contactValue = getContactValue(value);
  const hasValue = Boolean(contactValue);

  return (
    <div className="quick-action-btn-wrapper">
      <button
        type="button"
        className={classnames("btn-quick-action", { disabled: !hasValue })}
        onClick={() => {
          if (hasValue) window.open(href(contactValue));
        }}
        disabled={!hasValue}
        title={hasValue ? contactValue : `Chưa có ${label.toLowerCase()}`}
        aria-label={hasValue ? `${label}: ${contactValue}` : `Chưa có ${label.toLowerCase()}`}
      >
        <i className={icon} />
      </button>
      <span className="quick-action-label">{label}</span>
    </div>
  );
};

const PanelAction = ({ icon, label, onClick, disabled }) => (
  <div className="quick-action-btn-wrapper">
    <button
      type="button"
      className={classnames("btn-quick-action btn-quick-action-text", {
        disabled,
      })}
      onClick={onClick}
      disabled={disabled}
      title={disabled ? "Bạn không có quyền thực hiện thao tác này" : label}
    >
      <i className={icon} />
      <span>{label}</span>
    </button>
  </div>
);

const ProviderDetailPanel = ({
  provider = null,
  isOpen = false,
  onClose,
  onAddTransaction,
  canAddTransaction = true,
  onRefresh,
  toggleItemLogFunc,
  isModalMode = false,
  showCloseButton = true,
}) => {
  const [activeTab, setActiveTab] = useState("info");
  const [noteText, setNoteText] = useState("");
  const [notes, setNotes] = useState([]);
  const [isPayDebtOpen, setIsPayDebtOpen] = useState(false);
  const [payAmount, setPayAmount] = useState("");
  const [paymentMethod, setPaymentMethod] = useState("cash");
  const [payNote, setPayNote] = useState("");
  const [isPayingDebt, setIsPayingDebt] = useState(false);

  // Load notes from localStorage for this provider
  useEffect(() => {
    if (provider && provider.id) {
      const savedNotes = localStorage.getItem(`provider_notes_${provider.id}`);
      if (savedNotes) {
        setNotes(JSON.parse(savedNotes));
      } else {
        setNotes([]);
      }
    }
  }, [provider]);

  if (!provider) return null;

  const totalImport = (provider.company_provider_item || []).reduce(
    (acc, curr) => acc + (parseFloat(curr.price_income) || 0),
    0
  );
  const totalDebt = parseFloat(provider.total_price_pending) || 0;
  const isPaid = totalDebt === 0 && totalImport > 0;

  // Derive categories/groups
  const groupName =
    provider.company_provider_item && provider.company_provider_item.length > 0
      ? provider.company_provider_item[0].name
      : "Chưa phân loại";

  // Last purchase date
  const lastPurchaseDate =
    provider.company_provider_item && provider.company_provider_item.length > 0
      ? moment(
          Math.max(
            ...provider.company_provider_item.map((i) =>
              new Date(i.createdAt).getTime()
            )
          )
        ).format("DD/MM/YYYY")
      : "-";

  const handleAddNote = () => {
    if (!noteText.trim()) return;
    const newNote = {
      id: Date.now(),
      text: noteText,
      time: moment().format("HH:mm DD/MM/YYYY"),
    };
    const updatedNotes = [newNote, ...notes];
    setNotes(updatedNotes);
    localStorage.setItem(`provider_notes_${provider.id}`, JSON.stringify(updatedNotes));
    setNoteText("");
  };

  const handleDeleteNote = (noteId) => {
    const updatedNotes = notes.filter((n) => n.id !== noteId);
    setNotes(updatedNotes);
    localStorage.setItem(`provider_notes_${provider.id}`, JSON.stringify(updatedNotes));
  };

  const handleOpenPayDebt = () => {
    setPayAmount(String(totalDebt));
    setPaymentMethod("cash");
    setPayNote("");
    setIsPayDebtOpen(true);
  };

  const handleConfirmPayDebt = async () => {
    const rawVal = String(payAmount || "").replace(/[^\d]/g, "");
    const amountNum = Number(rawVal);
    if (!amountNum || amountNum <= 0) {
      NotificatioErrController("Vui lòng nhập số tiền thanh toán hợp lệ");
      return;
    }
    if (amountNum > totalDebt) {
      NotificatioErrController(`Số tiền trả không được lớn hơn tổng nợ (${AddCommaNumber(totalDebt)}đ)`);
      return;
    }

    try {
      setIsPayingDebt(true);
      const token = localStorage.getItem("access_token");
      const res = await axios.post(
        `${END_POINT}/company-provider/pay-debt`,
        {
          provider_id: provider.id,
          amount: amountNum,
          payment_method: paymentMethod,
          note: payNote,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data?.status === 200 || res.status === 200) {
        NotificatioSucController(res.data?.responseText || "Thanh toán nợ thành công");
        setIsPayDebtOpen(false);
        if (onRefresh) onRefresh();
      } else {
        NotificatioErrController(res.data?.responseText || "Không thể thực hiện thanh toán");
      }
    } catch (err) {
      NotificatioErrController(err.response?.data?.responseText || err.message || "Lỗi thanh toán nợ");
    } finally {
      setIsPayingDebt(false);
    }
  };

  return (
    <div
      className={classnames("provider-detail-panel", {
        open: isOpen,
        "provider-detail-panel-modal": isModalMode,
      })}
    >
      {/* Header */}
      <div className="detail-panel-header">
        {showCloseButton && (
          <button
            type="button"
            className="btn-close-detail"
            onClick={onClose}
            aria-label="Đóng chi tiết nhà cung cấp"
          >
            <i className="simple-icon-close" />
          </button>
        )}

        <div
          className="detail-avatar"
          style={{ backgroundColor: getAvatarBg(provider.name) }}
        >
          {getInitials(provider.name)}
        </div>

        <div className="detail-name-wrapper">
          <h4>{provider.name}</h4>
          <span
            className={classnames("status-badge", {
              paid: isPaid,
              debt: totalDebt > 0,
            })}
          >
            {totalDebt > 0 ? "Còn nợ" : isPaid ? "Đã trả hết" : "Chưa giao dịch"}
          </span>
          <div className="detail-phone">{provider.phone || "Chưa có SĐT"}</div>
        </div>

        {/* Quick Actions */}
        <div className="detail-quick-actions">
          <ContactAction
            icon="simple-icon-phone"
            label="Gọi điện"
            value={provider.phone}
            href={(value) => `tel:${value}`}
          />
          <ContactAction
            icon="simple-icon-bubble"
            label="Nhắn tin"
            value={provider.phone}
            href={(value) => `sms:${value}`}
          />
          <ContactAction
            icon="simple-icon-envelope"
            label="Email"
            value={provider.email}
            href={(value) => `mailto:${value}`}
          />
          <PanelAction
            icon="simple-icon-plus"
            label="Thêm giao dịch"
            onClick={() => onAddTransaction(provider)}
            disabled={!canAddTransaction}
          />
          <PanelAction
            icon="simple-icon-wallet"
            label="Trả nợ NCC"
            onClick={handleOpenPayDebt}
            disabled={totalDebt <= 0}
          />
        </div>
      </div>

      {/* Tabs Menu */}
      <div className="detail-tabs">
        <div
          className={classnames("detail-tab-item", { active: activeTab === "info" })}
          onClick={() => setActiveTab("info")}
        >
          Thông tin
        </div>
        <div
          className={classnames("detail-tab-item", { active: activeTab === "debt" })}
          onClick={() => setActiveTab("debt")}
        >
          Công nợ
        </div>
        <div
          className={classnames("detail-tab-item", { active: activeTab === "history" })}
          onClick={() => setActiveTab("history")}
        >
          Lịch sử
        </div>
        <div
          className={classnames("detail-tab-item", { active: activeTab === "notes" })}
          onClick={() => setActiveTab("notes")}
        >
          Ghi chú
        </div>
      </div>

      {/* Detail panel body content */}
      <div className="detail-panel-body">
        {/* Tab 1: Info */}
        {activeTab === "info" && (
          <div>
            <div className="detail-section-title">Thông tin chung</div>
            <div className="info-grid">
              <div className="info-row">
                <span className="info-label">Mã nhà cung cấp</span>
                <span className="info-value">NCC{provider.id?.substring(provider.id.length - 4).toUpperCase() || "0000"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Tên đầy đủ</span>
                <span className="info-value">{provider.name}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Số điện thoại</span>
                <span className="info-value">{provider.phone || "-"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Email</span>
                <span className="info-value">{provider.email || "-"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Địa chỉ</span>
                <span className="info-value">{provider.address || "-"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Mã số thuế</span>
                <span className="info-value">{provider.tax_code || "-"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Ghi chú</span>
                <span className="info-value">{provider.notes || "-"}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Nhóm hàng</span>
                <span className="info-value">{groupName}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Ngày tạo</span>
                <span className="info-value">
                  {provider.createdAt ? moment(provider.createdAt).format("DD/MM/YYYY") : "-"}
                </span>
              </div>
              <div className="info-row">
                <span className="info-label">Trạng thái</span>
                <span className="info-value text-success">Đang hoạt động</span>
              </div>
            </div>

            <div className="detail-section-title">Tổng quan</div>
            <div className="overview-grid">
              <div className="overview-card">
                <div className="overview-card-title">Tổng nhập</div>
                <div className="overview-card-value">{AddCommaNumber(totalImport)}đ</div>
              </div>
              <div className="overview-card">
                <div className="d-flex justify-content-between align-items-center">
                  <div className="overview-card-title">Công nợ hiện tại</div>
                  {totalDebt > 0 && (
                    <Button
                      color="danger"
                      size="xs"
                      outline
                      onClick={handleOpenPayDebt}
                      className="font-weight-bold"
                    >
                      Trả nợ
                    </Button>
                  )}
                </div>
                <div
                  className={classnames("overview-card-value", {
                    danger: totalDebt > 0,
                    success: totalDebt === 0,
                  })}
                >
                  {AddCommaNumber(totalDebt)}đ
                </div>
              </div>
              <div className="overview-card">
                <div className="overview-card-title">Số đơn hàng</div>
                <div className="overview-card-value">
                  {(provider.company_provider_item || []).length}
                </div>
              </div>
              <div className="overview-card">
                <div className="overview-card-title">Lần mua gần nhất</div>
                <div className="overview-card-value">{lastPurchaseDate}</div>
              </div>
            </div>
          </div>
        )}

        {/* Tab 2: Debt list */}
        {activeTab === "debt" && (
          <div>
            <div className="d-flex justify-content-between align-items-center mb-2">
              <div className="detail-section-title mb-0">Danh sách công nợ</div>
              {totalDebt > 0 && (
                <Button
                  color="danger"
                  size="xs"
                  onClick={handleOpenPayDebt}
                  className="font-weight-bold"
                >
                  <i className="simple-icon-wallet mr-1" /> Trả nợ NCC
                </Button>
              )}
            </div>
            {provider.company_provider_item && provider.company_provider_item.length > 0 ? (
              <div className="debt-items-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
                {provider.company_provider_item.map((item) => {
                  const debt = (parseFloat(item.price_income) || 0) - (parseFloat(item.price_outcome) || 0);
                  if (debt <= 0) return null;

                  return (
                    <div
                      key={item.id}
                      className="p-3 mb-2 border rounded"
                      style={{ backgroundColor: "#fdfefe" }}
                    >
                      <div className="d-flex justify-content-between align-items-center mb-1">
                        <strong className="text-dark">{item.name}</strong>
                        <span className="text-danger font-weight-bold">
                          {AddCommaNumber(debt)}đ
                        </span>
                      </div>
                      <div className="text-muted text-small mb-2">
                        SL: {item.quantity || 0} | Đơn giá: {AddCommaNumber(item.stock?.price_new || 0)}đ
                        <br />
                        Tổng: {AddCommaNumber(item.price_income)}đ | Đã trả: {AddCommaNumber(item.price_outcome || 0)}đ
                      </div>
                      <div className="text-right">
                        <Button
                          color="success"
                          outline
                          size="xs"
                          onClick={() => toggleItemLogFunc(item)}
                        >
                          Thanh toán
                        </Button>
                      </div>
                    </div>
                  );
                })}
                {provider.company_provider_item.every(
                  (item) => (parseFloat(item.price_income) || 0) - (parseFloat(item.price_outcome) || 0) <= 0
                ) && <div className="text-center text-muted p-4">Không còn công nợ nào cần thanh toán.</div>}
              </div>
            ) : (
              <div className="text-center text-muted p-4">Không có dữ liệu mặt hàng.</div>
            )}
          </div>
        )}

        {/* Tab 3: History */}
        {activeTab === "history" && (
          <div>
            <div className="detail-section-title">Lịch sử giao dịch</div>
            {provider.company_provider_item && provider.company_provider_item.length > 0 ? (
              <div className="purchase-history-list" style={{ maxHeight: "400px", overflowY: "auto" }}>
                {provider.company_provider_item.map((item) => (
                  <div key={item.id} className="p-3 mb-2 border-bottom">
                    <div className="d-flex justify-content-between align-items-center mb-1">
                      <strong className="text-dark">{item.name}</strong>
                      <span className="font-weight-bold text-dark">
                        {AddCommaNumber(item.price_income)}đ
                      </span>
                    </div>
                    <div className="text-muted text-small d-flex justify-content-between">
                      <span>SL: {item.quantity || 0} | SKU: {item.stock?.sku || "-"}</span>
                      <span>{item.createdAt ? moment(item.createdAt).format("DD/MM/YYYY") : "-"}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-muted p-4">Chưa có giao dịch nào.</div>
            )}
          </div>
        )}

        {/* Tab 4: Notes */}
        {activeTab === "notes" && (
          <div className="notes-container">
            <div className="detail-section-title">Ghi chú</div>
            <div className="note-textarea-wrapper">
              <Input
                type="textarea"
                className="note-input"
                placeholder="Nhập nội dung ghi chú..."
                value={noteText}
                onChange={(e) => setNoteText(e.target.value)}
              />
              <Button
                color="primary"
                size="sm"
                className="mt-2 btn-block"
                onClick={handleAddNote}
              >
                + Thêm ghi chú
              </Button>
            </div>

            <div className="mt-3">
              {notes.length === 0 ? (
                <div className="empty-notes">Chưa có ghi chú nào</div>
              ) : (
                <div className="notes-list">
                  {notes.map((note) => (
                    <div key={note.id} className="note-item">
                      <button
                        className="note-item-delete"
                        onClick={() => handleDeleteNote(note.id)}
                      >
                        ×
                      </button>
                      <div className="note-item-text">{note.text}</div>
                      <div className="note-item-time">{note.time}</div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Modal Trả nợ Nhà cung cấp */}
      <Modal
        isOpen={isPayDebtOpen}
        toggle={() => !isPayingDebt && setIsPayDebtOpen(false)}
        centered
        backdrop="static"
      >
        <ModalHeader toggle={() => !isPayingDebt && setIsPayDebtOpen(false)}>
          <i className="simple-icon-wallet mr-2 text-primary" />
          Thanh toán nợ Nhà cung cấp
        </ModalHeader>
        <ModalBody>
          <div className="p-3 mb-3 rounded" style={{ background: "#f8fafc", border: "1px solid #e2e8f0" }}>
            <div className="d-flex justify-content-between align-items-center mb-1">
              <span className="text-muted">Nhà cung cấp:</span>
              <strong className="text-dark">{provider.name}</strong>
            </div>
            <div className="d-flex justify-content-between align-items-center">
              <span className="text-muted">Tổng nợ hiện tại:</span>
              <strong className="text-danger" style={{ fontSize: "16px" }}>
                {AddCommaNumber(totalDebt)} đ
              </strong>
            </div>
          </div>

          <div className="form-group mb-3">
            <div className="d-flex justify-content-between align-items-center mb-1">
              <label className="font-weight-semibold text-small mb-0">Số tiền thanh toán:</label>
              <div className="d-flex gap-1">
                <Button
                  color="outline-primary"
                  size="xs"
                  onClick={() => setPayAmount(String(totalDebt))}
                  className="mr-1"
                >
                  Trả hết ({AddCommaNumber(totalDebt)}đ)
                </Button>
                <Button
                  color="outline-secondary"
                  size="xs"
                  onClick={() => setPayAmount(String(Math.round(totalDebt / 2)))}
                >
                  50%
                </Button>
              </div>
            </div>
            <div className="input-group">
              <Input
                type="text"
                className="font-weight-bold text-primary"
                style={{ fontSize: "16px" }}
                value={payAmount ? AddCommaNumber(payAmount) : ""}
                onChange={(e) => {
                  const num = e.target.value.replace(/[^\d]/g, "");
                  setPayAmount(num);
                }}
                placeholder="Nhập số tiền..."
              />
              <div className="input-group-append">
                <span className="input-group-text font-weight-bold">đ</span>
              </div>
            </div>
          </div>

          <div className="form-group mb-3">
            <label className="font-weight-semibold text-small mb-1">Phương thức thanh toán:</label>
            <div className="d-flex gap-3">
              <label className="mr-3 cursor-pointer">
                <input
                  type="radio"
                  name="payMethod"
                  value="cash"
                  checked={paymentMethod === "cash"}
                  onChange={() => setPaymentMethod("cash")}
                  className="mr-1"
                />
                Tiền mặt
              </label>
              <label className="cursor-pointer">
                <input
                  type="radio"
                  name="payMethod"
                  value="bank"
                  checked={paymentMethod === "bank"}
                  onChange={() => setPaymentMethod("bank")}
                  className="mr-1"
                />
                Chuyển khoản ngân hàng
              </label>
            </div>
          </div>

          <div className="form-group mb-3">
            <label className="font-weight-semibold text-small mb-1">Ghi chú thanh toán:</label>
            <Input
              type="text"
              value={payNote}
              onChange={(e) => setPayNote(e.target.value)}
              placeholder="VD: Trả nợ tiền hàng tháng 9..."
            />
          </div>

          <div className="d-flex justify-content-between align-items-center p-2 rounded mb-2" style={{ background: "#f1f5f9" }}>
            <span className="text-small text-muted font-weight-semibold">Dự kiến nợ còn lại:</span>
            <strong className="text-dark">
              {AddCommaNumber(Math.max(0, totalDebt - (Number(String(payAmount).replace(/[^\d]/g, "")) || 0)))} đ
            </strong>
          </div>

          <div className="alert alert-info py-2 px-3 mb-0 text-small">
            <i className="simple-icon-info mr-1" />
            Hệ thống sẽ tự động tạo một <strong>Phiếu chi</strong> trong Sổ quỹ cho khoản thanh toán này.
          </div>
        </ModalBody>
        <ModalFooter>
          <Button
            color="secondary"
            outline
            onClick={() => setIsPayDebtOpen(false)}
            disabled={isPayingDebt}
          >
            Hủy bỏ
          </Button>
          <Button
            color="primary"
            onClick={handleConfirmPayDebt}
            disabled={isPayingDebt || !payAmount || Number(payAmount) <= 0}
            className="font-weight-bold px-4"
          >
            {isPayingDebt ? (
              <span><i className="simple-icon-refresh spin mr-1" /> Đang xử lý...</span>
            ) : (
              <span><i className="simple-icon-check mr-1" /> Xác nhận thanh toán</span>
            )}
          </Button>
        </ModalFooter>
      </Modal>
    </div>
  );
};

export default React.memo(ProviderDetailPanel);
